import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  AlertTriangle,
  Bike,
  CheckCircle2,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  settlementService,
  type CompleteBasketResult,
} from '@services/settlement.service';
import type { BasketSplit } from '@services/moneyDashboard.service';
import { cn, formatCurrency } from '@lib/utils';
import { formatAmount, formatRate, formatTimestamp, moneyEquals, shortId } from '@pages/finance/ledgerFormat';

// ── Complete a stuck-but-paid transaction ──────────────────────────────────
// The buyer never confirmed, so escrow never released and nobody was paid. This
// runs the real release path — merchant, rider, commission and tax all move.
// Two deliberate steps, exactly like TreasuryOperationModal: preview (server
// computes the split, nothing moves) → confirm (the same call with preview off).

const inputClass =
  'w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors';

const labelClass =
  'block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5';

/** Both API clients funnel errors through `extractMessage`, so the API's own
 *  wording (ALREADY_RELEASED and friends) is already on `.message`. Surface it
 *  verbatim — an operator settling money must read what the backend said. */
function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

type Step = 'form' | 'preview' | 'done';

export interface CompleteTransactionModalProps {
  open: boolean;
  onClose: () => void;
  basket: BasketSplit;
  /** Shown so the operator can see they picked the right order. */
  orderId?: string | null;
  /** Called after a real (non-preview) completion succeeds. */
  onDone?: () => void;
}

const Fact = ({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'default' | 'muted' | 'danger';
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="shrink-0 text-[12px] text-[var(--text-muted)]">{label}</span>
    <span
      className={cn(
        'text-right text-[13px]',
        tone === 'danger'
          ? 'font-semibold text-[var(--state-danger)]'
          : tone === 'muted'
            ? 'text-[var(--text-muted)]'
            : 'text-[var(--text-primary)]',
      )}
    >
      {value}
    </span>
  </div>
);

/** The split the server says it will post (or did post). */
const SplitSummary = ({ result }: { result: CompleteBasketResult }) => {
  const { goods, delivery, allocations, reconciles } = result.split;
  const sum =
    allocations.merchant + allocations.commission + allocations.rider + allocations.tax;
  const sumChecks = moneyEquals(sum, allocations.total);
  const totalChecks = moneyEquals(allocations.total, result.totalAmount);

  return (
    <div className="space-y-4">
      {/* Goods */}
      <div className="space-y-2 rounded-2xl border border-[var(--border-subtle)] px-5 py-4">
        <h4 className="text-[13px] font-semibold text-[var(--text-primary)]">Goods</h4>
        <Fact label="Subtotal (VAT-inclusive)" value={formatCurrency(goods.subtotal)} />
        <Fact label="Ex-VAT base" value={formatCurrency(goods.exVatBase)} tone="muted" />
        <Fact label="Embedded VAT" value={formatCurrency(goods.embeddedVat)} tone="muted" />
        <Fact
          label={`Chekit commission${goods.commissionMode ? ` (${goods.commissionMode})` : ''}`}
          value={
            <>
              {formatCurrency(goods.commissionAmount)}
              <span className="ml-2 text-[12px] text-[var(--text-muted)]">
                @ {formatRate(goods.commissionRate)}
              </span>
            </>
          }
        />
        <Fact label="Merchant net" value={formatCurrency(goods.merchantNet)} />
      </div>

      {/* Delivery */}
      <div className="space-y-2 rounded-2xl border border-[var(--border-subtle)] px-5 py-4">
        <h4 className="text-[13px] font-semibold text-[var(--text-primary)]">Delivery</h4>
        <Fact label="Delivery fee" value={formatCurrency(delivery.fee)} />
        <Fact label="Rider payout" value={formatCurrency(delivery.driverPay)} />
        <Fact label="Chekit delivery cut" value={formatCurrency(delivery.chekitCommission)} />
        <Fact
          label="VAT on delivery"
          value={
            <>
              {formatCurrency(delivery.vat)}
              <span className="ml-2 text-[12px] text-[var(--text-muted)]">
                {delivery.taxModel ?? 'no tax model'}
                {delivery.vatPayer ? ` · payer ${delivery.vatPayer}` : ''}
              </span>
            </>
          }
        />
      </div>

      {/* Allocations — the money that actually moves */}
      <div
        className={cn(
          'space-y-2 rounded-2xl border px-5 py-4',
          reconciles && sumChecks && totalChecks
            ? 'border-[var(--border-subtle)]'
            : 'border-[var(--state-danger-border)]',
        )}
      >
        <h4 className="text-[13px] font-semibold text-[var(--text-primary)]">
          Where the money goes
        </h4>
        <Fact label="Merchant" value={formatCurrency(allocations.merchant)} />
        <Fact label="Chekit commission" value={formatCurrency(allocations.commission)} />
        <Fact label="Rider" value={formatCurrency(allocations.rider)} />
        <Fact label="Tax float" value={formatCurrency(allocations.tax)} />
        <div className="border-t border-[var(--border-subtle)] pt-2">
          <Fact
            label="Total allocated"
            value={formatCurrency(allocations.total)}
            tone={sumChecks && totalChecks ? 'default' : 'danger'}
          />
          <Fact label="Basket total" value={formatCurrency(result.totalAmount)} tone="muted" />
          <p className="mt-1 font-mono text-[11px] text-[var(--text-muted)]">
            {formatAmount(allocations.merchant)} + {formatAmount(allocations.commission)} +{' '}
            {formatAmount(allocations.rider)} + {formatAmount(allocations.tax)} ={' '}
            {formatAmount(sum)}
          </p>
        </div>

        {allocations.riderFoldedIntoCommission && (
          <p className="flex items-start gap-2 text-[12px] text-[var(--state-warning)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            No rider earnings wallet could be resolved — the rider share will be folded
            into Chekit commission rather than paid to a rider.
          </p>
        )}

        {!(reconciles && sumChecks && totalChecks) && (
          <p className="flex items-start gap-2 text-[12px] text-[var(--state-danger)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            This split does not reconcile to the basket total — off by{' '}
            {formatAmount(allocations.total - result.totalAmount)}.
          </p>
        )}
      </div>
    </div>
  );
};

export function CompleteTransactionModal({
  open,
  onClose,
  basket,
  orderId,
  onDone,
}: CompleteTransactionModalProps) {
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [previewResult, setPreviewResult] = useState<CompleteBasketResult | null>(null);
  const [appliedResult, setAppliedResult] = useState<CompleteBasketResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setStep('form');
    setPreviewResult(null);
    setAppliedResult(null);
    setError(null);
  }, [open, basket.basketId]);

  const reasonValid = reason.trim().length > 0;

  const previewMutation = useMutation({
    mutationFn: () =>
      settlementService.completeBasket(basket.basketId, {
        reason: reason.trim(),
        preview: true,
      }),
    onSuccess: (result) => {
      setPreviewResult(result);
      setError(null);
      setStep('preview');
    },
    onError: (err) => setError(apiMessage(err, 'Could not preview this settlement.')),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      settlementService.completeBasket(basket.basketId, {
        reason: reason.trim(),
        preview: false,
      }),
    onSuccess: (result) => {
      setAppliedResult(result);
      setError(null);
      setStep('done');
      toast.success('Transaction completed — escrow released.');
      // The split, every wallet balance and the money dashboard are now stale.
      queryClient.invalidateQueries({ queryKey: ['money-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['ledger'] });
      queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onDone?.();
    },
    onError: (err) => setError(apiMessage(err, 'Could not complete this transaction.')),
  });

  const busy = previewMutation.isPending || applyMutation.isPending;

  if (!open) return null;

  const hasRider = !!basket.riderId;
  const riderFromServer = (step === 'done' ? appliedResult : previewResult)?.rider ?? null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
      <div className="my-8 w-full max-w-2xl rounded-3xl bg-[var(--bg-surface)] shadow-lift">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-8 py-6">
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold text-[var(--text-primary)]">
              Complete this transaction
            </h2>
            <p className="mt-1 truncate text-[13px] text-[var(--text-muted)]">
              {basket.businessName ?? 'Unnamed merchant'} · basket {shortId(basket.basketId)}
              {orderId ? ` · order ${shortId(orderId)}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-8 py-6">
          {/* ── Step 1: what this does, who delivered, why ──────────────── */}
          {step === 'form' && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-4">
                <ShieldAlert
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-danger)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    This releases real money
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    Escrow for this basket will be released and split to the merchant, the
                    rider, Chekit commission and the tax float — exactly as if the buyer
                    had confirmed. It cannot be undone from here; reversing it would take
                    a separate treasury operation.
                  </p>
                </div>
              </div>

              {/* Basket facts */}
              <div className="space-y-2 rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                <Fact label="Basket total" value={formatCurrency(basket.totalAmount)} />
                <Fact
                  label="Basket status"
                  value={<span className="capitalize">{basket.status.replace(/_/g, ' ')}</span>}
                />
                <Fact label="Delivered at" value={formatTimestamp(basket.deliveredAt)} />
                <Fact
                  label="Delivery provider"
                  value={basket.deliveryProviderType ?? 'not recorded'}
                  tone={basket.deliveryProviderType ? 'default' : 'muted'}
                />
              </div>

              {/* Who delivered it */}
              <div
                className={cn(
                  'flex items-start gap-3 rounded-2xl border px-5 py-4',
                  hasRider
                    ? 'border-[var(--border-subtle)]'
                    : 'border-[var(--state-warning-border)] bg-[var(--state-warning-bg)]',
                )}
              >
                {hasRider ? (
                  <Bike
                    className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-muted)]"
                    strokeWidth={1.5}
                  />
                ) : (
                  <AlertTriangle
                    className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
                    strokeWidth={1.5}
                  />
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    {hasRider ? 'Rider who delivered' : 'No rider attached to this basket'}
                  </p>
                  {hasRider ? (
                    <>
                      <p className="text-[13px] text-[var(--text-secondary)]">
                        {basket.riderName ?? 'Name not on file'}
                      </p>
                      <p className="font-mono text-[12px] text-[var(--text-muted)]">
                        rider {shortId(basket.riderId)}
                        {basket.riderUserId ? ` · user ${shortId(basket.riderUserId)}` : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-[13px] text-[var(--text-secondary)]">
                      Nothing on this basket says who delivered it. The rider share has
                      nowhere to go and will be folded into Chekit commission. Check the
                      delivery record before settling.
                    </p>
                  )}
                </div>
              </div>

              {/* Reason — mandatory */}
              <div>
                <label className={labelClass} htmlFor="settlement-reason">
                  Reason *
                </label>
                <textarea
                  id="settlement-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Why is this being settled manually? Stored on the release snapshot."
                  className={cn(inputClass, 'resize-none')}
                />
                {!reasonValid && (
                  <p className="mt-2 text-[12px] text-[var(--text-muted)]">
                    Required — this is recorded against your name on the money movement.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Step 2: exactly what will be posted ─────────────────────── */}
          {step === 'preview' && previewResult && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-warning-border)] bg-[var(--state-warning-bg)] px-5 py-4">
                <ShieldAlert
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    Nothing has moved yet
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    This is the split the server computed for a dry run. Check it, then
                    confirm to release escrow for real.
                  </p>
                </div>
              </div>

              {riderFromServer ? (
                <div className="space-y-2 rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                  <Fact
                    label="Rider being paid"
                    value={riderFromServer.name ?? shortId(riderFromServer.riderId)}
                  />
                  <Fact
                    label="Rider id"
                    value={<span className="font-mono">{shortId(riderFromServer.riderId)}</span>}
                    tone="muted"
                  />
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-warning-border)] bg-[var(--state-warning-bg)] px-5 py-4">
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-warning)]"
                    strokeWidth={1.5}
                  />
                  <p className="text-[13px] text-[var(--text-secondary)]">
                    The server resolved no rider for this basket — nobody will be paid a
                    rider share.
                  </p>
                </div>
              )}

              <SplitSummary result={previewResult} />

              <div className="space-y-2 rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                <Fact label="Basket status after" value={previewResult.basketStatus} />
                <Fact label="Order status after" value={previewResult.orderStatus ?? '—'} />
                <Fact label="Payment status" value={previewResult.paymentStatus ?? '—'} />
                <Fact label="Reason" value={reason.trim()} tone="muted" />
              </div>
            </div>
          )}

          {/* ── Step 3: what actually happened ──────────────────────────── */}
          {step === 'done' && appliedResult && (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-2xl border border-[var(--state-success-border)] bg-[var(--state-success-bg)] px-5 py-4">
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-success)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    Transaction completed
                  </p>
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    {formatCurrency(appliedResult.totalAmount)} released from escrow and
                    split as a balanced journal entry.
                    {appliedResult.completedBy?.name
                      ? ` Recorded against ${appliedResult.completedBy.name}.`
                      : ''}
                  </p>
                </div>
              </div>

              {appliedResult.journalEntryId && (
                <div className="rounded-2xl bg-[var(--bg-base)] px-5 py-4">
                  <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Journal entry
                  </div>
                  <div className="mt-1 break-all font-mono text-[13px] text-[var(--text-primary)]">
                    {appliedResult.journalEntryId}
                  </div>
                </div>
              )}

              <SplitSummary result={appliedResult} />
            </div>
          )}

          {/* API errors — ALREADY_RELEASED, ORDER_NOT_PAID, BASKET_TERMINAL */}
          {error && (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-4">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]"
                strokeWidth={1.5}
              />
              <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-8 py-5">
          <p className="text-[12px] text-[var(--text-muted)]">
            {step === 'form' && 'Step 1 of 2 — preview first, then confirm.'}
            {step === 'preview' && 'Step 2 of 2 — confirm to release real money.'}
            {step === 'done' && 'Done.'}
          </p>

          <div className="flex items-center gap-3">
            {step === 'form' && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => previewMutation.mutate()}
                  disabled={!reasonValid || busy}
                  className="rounded-full bg-[var(--text-primary)] px-5 py-2.5 text-[14px] font-medium text-[var(--bg-surface)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {previewMutation.isPending ? 'Previewing...' : 'Preview the split'}
                </button>
              </>
            )}

            {step === 'preview' && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setStep('form');
                    setPreviewResult(null);
                    setError(null);
                  }}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => applyMutation.mutate()}
                  disabled={busy}
                  className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-semibold text-[var(--state-danger)] transition-colors hover:border-[var(--state-danger)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {applyMutation.isPending ? 'Releasing...' : 'Confirm — release the money'}
                </button>
              </>
            )}

            {step === 'done' && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full bg-[var(--text-primary)] px-5 py-2.5 text-[14px] font-medium text-[var(--bg-surface)] transition-opacity hover:opacity-90"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
